/* eslint-disable @typescript-eslint/no-require-imports */
import { FontSource } from 'expo-font';
import { Platform } from 'react-native';
import interactive from 'shared/styles/color/interactive';
import text from 'shared/styles/color/text';
import { type Typography } from 'shared/styles/types/typography';

// Use postscript names for fonts
export const VaroFontNames = {
  National2Compressed: 'National2Compressed-Bold',
  NeueHaasGroteskDisplayProRomanNormal: 'NeueHaasGroteskDispW05-55Rm',
  NeueHaasGroteskDisplayProMediumNormal: 'NeueHaasGroteskDispW05-65Md',
  VaroHand: 'VaroHandRegular',
};

export const VaroFontMap = {
  [VaroFontNames.VaroHand]:
    require('assets/fonts/VaroHandRegular.otf') as FontSource,
  [VaroFontNames.National2Compressed]:
    require('assets/fonts/National2Compressed-Bold.ttf') as FontSource,
  [VaroFontNames.NeueHaasGroteskDisplayProMediumNormal]:
    require('assets/fonts/NeueHaasGroteskDispW05-65Md.ttf') as FontSource,
  [VaroFontNames.NeueHaasGroteskDisplayProRomanNormal]:
    require('assets/fonts/NeueHaasGroteskDispW05-55Rm.ttf') as FontSource,
};

const typography: Typography = {
  expressive: {
    xxxl: {
      color: text.primary,
      fontFamily: VaroFontNames.National2Compressed,
      fontSize: 80,
      lineHeight: 64,
      letterSpacing: -0.8,
      textTransform: 'uppercase',
      ...Platform.select({
        native: {
          paddingTop: 80 - 64 + (80 - 64) / 2,
        },
      }),
    },
    xxl: {
      color: text.primary,
      fontFamily: VaroFontNames.National2Compressed,
      fontSize: 72,
      lineHeight: 58,
      letterSpacing: -0.72,
      textTransform: 'uppercase',
      ...Platform.select({
        native: {
          paddingTop: 72 - 58 + (72 - 58) / 2,
        },
      }),
    },
    xl: {
      color: text.primary,
      fontFamily: VaroFontNames.National2Compressed,
      fontSize: 60,
      lineHeight: 48,
      letterSpacing: -0.6,
      textTransform: 'uppercase',
      ...Platform.select({
        native: {
          paddingTop: 60 - 48 + (60 - 48) / 2,
        },
      }),
    },
    lg: {
      color: text.primary,
      fontFamily: VaroFontNames.National2Compressed,
      fontSize: 52,
      lineHeight: 42,
      letterSpacing: -0.52,
      textTransform: 'uppercase',
      ...Platform.select({
        native: {
          paddingTop: 52 - 42 + (52 - 42) / 2,
        },
      }),
    },
    md: {
      color: text.primary,
      fontFamily: VaroFontNames.National2Compressed,
      fontSize: 48,
      lineHeight: 38,
      letterSpacing: -0.48,
      textTransform: 'uppercase',
      ...Platform.select({
        native: {
          paddingTop: 48 - 38 + (48 - 38) / 2,
        },
      }),
    },
    sm: {
      color: text.primary,
      fontFamily: VaroFontNames.National2Compressed,
      fontSize: 44,
      lineHeight: 35,
      letterSpacing: -0.44,
      textTransform: 'uppercase',
      ...Platform.select({
        native: {
          paddingTop: 44 - 35 + (44 - 35) / 2,
        },
      }),
    },
    xs: {
      color: text.primary,
      fontFamily: VaroFontNames.National2Compressed,
      fontSize: 36,
      lineHeight: 29,
      letterSpacing: -0.36,
      textTransform: 'uppercase',
      ...Platform.select({
        native: {
          paddingTop: 36 - 29 + (36 - 29) / 2,
        },
      }),
    },
    xxs: {
      color: text.primary,
      fontFamily: VaroFontNames.National2Compressed,
      fontSize: 32,
      lineHeight: 26,
      textTransform: 'uppercase',
      ...Platform.select({
        native: {
          paddingTop: 32 - 26 + (32 - 26) / 2,
        },
      }),
    },
  },
  title: {
    xl: {
      color: text.primary,
      fontFamily: VaroFontNames.NeueHaasGroteskDisplayProMediumNormal,
      fontSize: 34,
      lineHeight: 38,
    },
    lg: {
      color: text.primary,
      fontFamily: VaroFontNames.NeueHaasGroteskDisplayProMediumNormal,
      fontSize: 28,
      lineHeight: 32,
    },
    md: {
      color: text.primary,
      fontFamily: VaroFontNames.NeueHaasGroteskDisplayProMediumNormal,
      fontSize: 22,
      lineHeight: 26,
    },
    sm: {
      color: text.primary,
      fontFamily: VaroFontNames.NeueHaasGroteskDisplayProMediumNormal,
      fontSize: 18,
      lineHeight: 22,
    },
  },
  body: {
    base: {
      color: text.primary,
      fontFamily: VaroFontNames.NeueHaasGroteskDisplayProRomanNormal,
      fontSize: 16,
      lineHeight: 22,
      letterSpacing: 0.32,
    },
    baseBold: {
      color: text.primary,
      fontFamily: VaroFontNames.NeueHaasGroteskDisplayProMediumNormal,
      fontSize: 16,
      lineHeight: 22,
      letterSpacing: 0.32,
    },
    sm: {
      color: text.primary,
      fontFamily: VaroFontNames.NeueHaasGroteskDisplayProRomanNormal,
      fontSize: 14,
      lineHeight: 20,
      letterSpacing: 0.28,
    },
    smBold: {
      color: text.primary,
      fontFamily: VaroFontNames.NeueHaasGroteskDisplayProMediumNormal,
      fontSize: 14,
      lineHeight: 20,
      letterSpacing: 0.28,
    },
    xs: {
      color: text.primary,
      fontFamily: VaroFontNames.NeueHaasGroteskDisplayProRomanNormal,
      fontSize: 12,
      lineHeight: 16,
      letterSpacing: 0.24,
    },
    xsBold: {
      color: text.primary,
      fontFamily: VaroFontNames.NeueHaasGroteskDisplayProMediumNormal,
      fontSize: 12,
      lineHeight: 16,
      letterSpacing: 0.24,
    },
  },
  hyperlink: {
    base: {
      fontFamily: VaroFontNames.NeueHaasGroteskDisplayProMediumNormal,
      fontSize: 16,
      lineHeight: 22,
      letterSpacing: 0.32,
      color: interactive.primary,
      textDecorationLine: 'underline',
    },
    sm: {
      fontFamily: VaroFontNames.NeueHaasGroteskDisplayProMediumNormal,
      fontSize: 14,
      lineHeight: 20,
      letterSpacing: 0.28,
      color: interactive.primary,
      textDecorationLine: 'underline',
    },
    xs: {
      fontFamily: VaroFontNames.NeueHaasGroteskDisplayProMediumNormal,
      fontSize: 12,
      lineHeight: 16,
      letterSpacing: 0.24,
      color: interactive.primary,
      textDecorationLine: 'underline',
    },
  },
  varoHand: {
    lg: {
      color: text.primary,
      fontFamily: VaroFontNames.VaroHand,
      fontSize: 36,
      textTransform: 'lowercase',
      paddingLeft: 3, //TODO: we need to fix this for android
    },
    md: {
      color: text.primary,
      fontFamily: VaroFontNames.VaroHand,
      fontSize: 28,
      textTransform: 'lowercase',
      paddingLeft: 3, //TODO: we need to fix this for android
    },
    sm: {
      color: text.primary,
      fontFamily: VaroFontNames.VaroHand,
      fontSize: 20,
      textTransform: 'lowercase',
      paddingLeft: 2, //TODO: we need to fix this for android
    },
  },
  component: {
    button: {
      color: interactive.primary,
      fontFamily: VaroFontNames.NeueHaasGroteskDisplayProMediumNormal,
      fontSize: 14,
      lineHeight: 24,
      letterSpacing: 0.02,
    },
    input: {
      color: text.primary,
      fontFamily: VaroFontNames.NeueHaasGroteskDisplayProRomanNormal,
      fontSize: 16,
      minHeight: 24, // Temp fix for inputs - TODO: JT-157641
      letterSpacing: 0.32,
      padding: 0,
    },
  },
};

export default typography;
